import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, ClipboardCheck, Clipboard } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * /debug — Network + CORS diagnostic page.
 *
 * Always mounted (no auth required) so it works even when login fails.
 * It performs a plain `fetch` against `/api/health` using the *same* URL
 * and credentials configuration that the app uses in production. Everything
 * is surfaced to the user: URL called, status, headers, body, raw error.
 *
 * Use this page to debug the "Impossible de joindre le serveur" message in
 * production — share the output with support if the check fails.
 */
export default function Debug() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    setLoading(true);
    setCopied(false);
    const started = Date.now();
    const url = `${BACKEND_URL}/api/health`;
    const out = {
      frontend_origin: typeof window !== "undefined" ? window.location.origin : "(server)",
      backend_url: BACKEND_URL,
      target: url,
      started_at: new Date().toISOString(),
    };
    try {
      const res = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      out.http_status = res.status;
      out.http_ok = res.ok;
      out.response_headers = Object.fromEntries(res.headers.entries());
      try { out.body = await res.json(); } catch (_) { out.body = await res.text(); }
    } catch (err) {
      out.error = true;
      out.error_name = err?.name || "Error";
      out.error_message = err?.message || String(err);
      out.error_stack = err?.stack;
    }
    out.duration_ms = Date.now() - started;
    setResult(out);
    setLoading(false);
    // eslint-disable-next-line no-console
    console.log("[Debug /health]", out);
  };

  useEffect(() => { run(); }, []);

  const copy = () => {
    if (!result) return;
    navigator.clipboard.writeText(JSON.stringify(result, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const ok = result && !result.error && result.http_ok;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14 lg:px-10" data-testid="debug-page">
      <p className="text-xs uppercase tracking-widest text-primary">Diagnostic réseau</p>
      <h1 className="mt-2 font-[Outfit] text-3xl font-normal tracking-tight sm:text-4xl">État de la connexion backend</h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Cette page teste la communication entre votre domaine et le serveur Saor.
        Partagez le résultat complet avec le support pour résoudre un souci de connexion.
      </p>

      <div className="mt-6 grid gap-3 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6">
        <Row label="Origine du site (frontend)" value={typeof window !== "undefined" ? window.location.origin : "—"} />
        <Row label="URL backend configurée" value={BACKEND_URL || "❌ absente — REACT_APP_BACKEND_URL manquante"} mono />
        <Row label="Endpoint testé" value={`${BACKEND_URL}/api/health`} mono />
      </div>

      {loading && (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-sm" data-testid="debug-loading">
          <Loader2 className="h-4 w-4 animate-spin text-primary" /> Test en cours...
        </div>
      )}

      {result && !loading && (
        <div className={`mt-4 rounded-2xl border p-5 shadow-sm sm:p-6 ${ok ? "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/5" : "border-destructive/40 bg-destructive/5"}`} data-testid="debug-result">
          <div className="flex items-center gap-2">
            {ok ? <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))]" /> : <XCircle className="h-5 w-5 text-destructive" />}
            <p className="font-[Outfit] text-base font-medium">
              {ok ? "Backend joignable ✓" : result.error ? "Backend injoignable (erreur réseau / CORS)" : `Backend a répondu HTTP ${result.http_status}`}
            </p>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Durée : {result.duration_ms} ms</p>

          {result.error && (
            <div className="mt-3 space-y-1 text-xs">
              <p><strong>Erreur :</strong> {result.error_name} · {result.error_message}</p>
              <p className="text-muted-foreground">
                Causes probables : mauvais <code>REACT_APP_BACKEND_URL</code>, CORS bloquant,
                backend hors ligne ou adresse inaccessible depuis ce domaine.
              </p>
            </div>
          )}

          <pre className="mt-4 max-h-80 overflow-auto rounded-lg bg-background p-3 text-[11px] leading-5" data-testid="debug-json">
            {JSON.stringify(result, null, 2)}
          </pre>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={copy} className="gap-1.5" data-testid="debug-copy">
              {copied ? <ClipboardCheck className="h-3.5 w-3.5" /> : <Clipboard className="h-3.5 w-3.5" />}
              {copied ? "Copié" : "Copier le diagnostic"}
            </Button>
            <Button size="sm" onClick={run} data-testid="debug-retry" className="bg-primary hover:bg-primary/90">Relancer le test</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-xs uppercase tracking-widest text-muted-foreground">{label}</span>
      <span className={`break-all text-sm ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
